import React, { FunctionComponent } from 'react';

import {
  CoachSettingsWidgetGotoLinks,
  CoachSettingsWidgetGotoLinksStatus,
  GotoLinksSettingsValue,
} from './GotoLinksSettings';
import {
  CoachSettingsWidgetActions,
  CoachSettingsWidgetActionsStatus,
  ActionsSettingsValue,
} from './ActionsSettings';
import {
  CoachSettingsWidgetPictureAndTags,
  CoachSettingsWidgetPictureAndTagsStatus,
  PictureAndTagsSettingsValue,
} from './PictureAndTagsSettings';
import {
  CoachSettingsWidgetDetailsContact,
  CoachSettingsWidgetDetailsContactStatus,
  DetailsContactSettingsValue,
} from './DetailsContactSettings';
import {
  CoachSettingsWidgetDetailsBasic,
  CoachSettingsWidgetDetailsBasicStatus,
  DetailsBasicSettingsValue,
} from './DetailsBasicSettings';
import {
  CoachSettingsWidgetDetailsExtra,
  CoachSettingsWidgetDetailsExtraStatus,
  DetailsExtraSettingsValue,
} from './DetailsExtraSettings';
import {
  CoachSettingsWidgetConsentInformation,
  CoachSettingsWidgetConsentInformationStatus,
  ConsentInformationSettingsValue,
} from './ConsentInformationSettings';
import {
  CoachSettingsWidgetNotificationInformation,
  CoachSettingsWidgetNotificationInformationStatus,
  NotificationInformationSettingsValue,
} from './NotificationInformationSettings';
import {
  CoachSettingsWidgetMollieLinksSmall,
  CoachSettingsWidgetMollieLinksSmallStatus,
  MollieLinksSmallSettingsValue,
} from './MollieLinksSmallSettings';
import {
  CoachSettingsWidgetAppointmentsSmall,
  CoachSettingsWidgetAppointmentsSmallStatus,
  AppointmentsSmallSettingsValue,
} from './AppointmentsSmallSettings';
import {
  CoachSettingsWidgetCoursesSmall,
  CoachSettingsWidgetCoursesSmallStatus,
  CoursesSmallSettingsValue,
} from './CoursesSmallSettings';
import {
  CoachSettingsWidgetQuestionnairesSmall,
  CoachSettingsWidgetQuestionnairesSmallStatus,
  QuestionnairesSmallSettingsValue,
} from './QuestionnairesSmallSettings';
import {
  CoachSettingsWidgetMeasuresSmall,
  CoachSettingsWidgetMeasuresSmallStatus,
  MeasuresSmallSettingsValue,
} from './MeasuresSmallSettings';
import {
  CoachSettingsWidgetDiarySmall,
  CoachSettingsWidgetDiarySmallStatus,
  DiarySmallSettingsValue,
} from './DiarySmallSettings';
import {
  CoachSettingsWidgetFilesSmall,
  CoachSettingsWidgetFilesSmallStatus,
  FilesSmallSettingsValue,
} from './FilesSmallSettings';
import {
  CoachSettingsWidgetBasicIntake,
  CoachSettingsWidgetBasicIntakeStatus,
  BasicIntakeSettingsValue,
} from './BasicIntakeSettings';
import {
  CoachSettingsWidgetBasicTreatment,
  CoachSettingsWidgetBasicTreatmentStatus,
  BasicTreatmentSettingsValue,
} from './BasicTreatmentSettings';
import {
  CoachSettingsWidgetBasicMeasureUploads,
  CoachSettingsWidgetBasicMeasureUploadsStatus,
  BasicMeasureUploadsSettingsValue,
} from './BasicMeasureUploadsSettings';
import {
  CoachSettingsWidgetBasicAdvice,
  CoachSettingsWidgetBasicAdviceStatus,
  BasicAdviceSettingsValue,
} from './BasicAdviceSettings';
import {
  CoachSettingsWidgetBasicAdviceUploads,
  CoachSettingsWidgetBasicAdviceUploadsStatus,
  BasicAdviceUploadsSettingsValue,
} from './BasicAdviceUploadsSettings';
import {
  CoachSettingsWidgetTimeline,
  CoachSettingsWidgetTimelineStatus,
  TimelineSettingsValue,
} from './TimelineSettings';
import {
  CoachSettingsWidgetStatus,
  CoachSettingsWidgetStatusStatus,
  StatusSettingsValue,
} from './StatusSettings';
import {
  CoachSettingsWidgetCommunicationNutri,
  CoachSettingsWidgetCommunicationNutriStatus,
  CoachSettingsWidgetCommunicationEhealth,
  CoachSettingsWidgetCommunicationEhealthStatus,
  CoachSettingsWidgetCommunicationZorg,
  CoachSettingsWidgetCommunicationZorgStatus,
  CommunicationSmallSettingsValue,
} from './CommunicationSmallSettings';

// Add a key here once its settings UI is implemented.
// This is the single source of truth for both "does the gear show?"
// and "which component renders in the settings dialog?".
export const WIDGETS_WITH_SETTINGS: string[] = [
  'goto_links',
  'actions',
  'picture_and_tags',
  'details_contact',
  'details_basic',
  'details_extra',
  'consent_information',
  'notification_information',
  'mollie_links_small',
  'appointments_small',
  'courses_small',
  'questionnaires_small',
  'files_small',
  'measures_small',
  'diary_small',
  'communication_nutri',
  'communication_ehealth',
  'communication_zorg',
  'basic_intake',
  'basic_treatment',
  'basic_measure_uploads',
  'basic_advice',
  'basic_advice_uploads',
  'timeline',
  'status',
];

type SettingsProps = {
  value: any;
  onChange: (v: any) => void;
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

type SettingsComponent = FunctionComponent<SettingsProps>;

// Maps a widget key to its settings component.
// Returns null when a widget has no settings UI (defensive — the gear is only
// shown for keys in WIDGETS_WITH_SETTINGS, but this keeps the dialog safe).
export const getWidgetSettingsComponent = (widgetKey: string | null): SettingsComponent | null => {
  switch (widgetKey) {
    case 'goto_links':
      return CoachSettingsWidgetGotoLinks as SettingsComponent;
    case 'actions':
      return CoachSettingsWidgetActions as SettingsComponent;
    case 'picture_and_tags':
      return CoachSettingsWidgetPictureAndTags as SettingsComponent;
    case 'details_contact':
      return CoachSettingsWidgetDetailsContact as SettingsComponent;
    case 'details_basic':
      return CoachSettingsWidgetDetailsBasic as SettingsComponent;
    case 'details_extra':
      return CoachSettingsWidgetDetailsExtra as SettingsComponent;
    case 'consent_information':
      return CoachSettingsWidgetConsentInformation as SettingsComponent;
    case 'notification_information':
      return CoachSettingsWidgetNotificationInformation as SettingsComponent;
    case 'mollie_links_small':
      return CoachSettingsWidgetMollieLinksSmall as SettingsComponent;
    case 'appointments_small':
      return CoachSettingsWidgetAppointmentsSmall as SettingsComponent;
    case 'courses_small':
      return CoachSettingsWidgetCoursesSmall as SettingsComponent;
    case 'questionnaires_small':
      return CoachSettingsWidgetQuestionnairesSmall as SettingsComponent;
    case 'files_small':
      return CoachSettingsWidgetFilesSmall as SettingsComponent;
    case 'measures_small':
      return CoachSettingsWidgetMeasuresSmall as SettingsComponent;
    case 'diary_small':
      return CoachSettingsWidgetDiarySmall as SettingsComponent;
    case 'communication_nutri':
      return CoachSettingsWidgetCommunicationNutri as SettingsComponent;
    case 'communication_ehealth':
      return CoachSettingsWidgetCommunicationEhealth as SettingsComponent;
    case 'communication_zorg':
      return CoachSettingsWidgetCommunicationZorg as SettingsComponent;
    case 'basic_intake':
      return CoachSettingsWidgetBasicIntake as SettingsComponent;
    case 'basic_treatment':
      return CoachSettingsWidgetBasicTreatment as SettingsComponent;
    case 'basic_measure_uploads':
      return CoachSettingsWidgetBasicMeasureUploads as SettingsComponent;
    case 'basic_advice':
      return CoachSettingsWidgetBasicAdvice as SettingsComponent;
    case 'basic_advice_uploads':
      return CoachSettingsWidgetBasicAdviceUploads as SettingsComponent;
    case 'timeline':
      return CoachSettingsWidgetTimeline as SettingsComponent;
    case 'status':
      return CoachSettingsWidgetStatus as SettingsComponent;
    default:
      return null;
  }
};

// --- Per-widget status (read-only summary shown on the placed box) ---
// A widget may optionally expose a status component. It receives the widget's
// persisted settings value and renders a compact, read-only summary.
type StatusProps = {
  value: any;
  order?: string[];
};

type StatusComponent = FunctionComponent<StatusProps>;

export const getWidgetStatusComponent = (widgetKey: string | null): StatusComponent | null => {
  switch (widgetKey) {
    case 'goto_links':
      return CoachSettingsWidgetGotoLinksStatus as StatusComponent;
    case 'actions':
      return CoachSettingsWidgetActionsStatus as StatusComponent;
    case 'picture_and_tags':
      return CoachSettingsWidgetPictureAndTagsStatus as StatusComponent;
    case 'details_contact':
      return CoachSettingsWidgetDetailsContactStatus as StatusComponent;
    case 'details_basic':
      return CoachSettingsWidgetDetailsBasicStatus as StatusComponent;
    case 'details_extra':
      return CoachSettingsWidgetDetailsExtraStatus as StatusComponent;
    case 'consent_information':
      return CoachSettingsWidgetConsentInformationStatus as StatusComponent;
    case 'notification_information':
      return CoachSettingsWidgetNotificationInformationStatus as StatusComponent;
    case 'mollie_links_small':
      return CoachSettingsWidgetMollieLinksSmallStatus as StatusComponent;
    case 'appointments_small':
      return CoachSettingsWidgetAppointmentsSmallStatus as StatusComponent;
    case 'courses_small':
      return CoachSettingsWidgetCoursesSmallStatus as StatusComponent;
    case 'questionnaires_small':
      return CoachSettingsWidgetQuestionnairesSmallStatus as StatusComponent;
    case 'files_small':
      return CoachSettingsWidgetFilesSmallStatus as StatusComponent;
    case 'measures_small':
      return CoachSettingsWidgetMeasuresSmallStatus as StatusComponent;
    case 'diary_small':
      return CoachSettingsWidgetDiarySmallStatus as StatusComponent;
    case 'communication_nutri':
      return CoachSettingsWidgetCommunicationNutriStatus as StatusComponent;
    case 'communication_ehealth':
      return CoachSettingsWidgetCommunicationEhealthStatus as StatusComponent;
    case 'communication_zorg':
      return CoachSettingsWidgetCommunicationZorgStatus as StatusComponent;
    case 'basic_intake':
      return CoachSettingsWidgetBasicIntakeStatus as StatusComponent;
    case 'basic_treatment':
      return CoachSettingsWidgetBasicTreatmentStatus as StatusComponent;
    case 'basic_measure_uploads':
      return CoachSettingsWidgetBasicMeasureUploadsStatus as StatusComponent;
    case 'basic_advice':
      return CoachSettingsWidgetBasicAdviceStatus as StatusComponent;
    case 'basic_advice_uploads':
      return CoachSettingsWidgetBasicAdviceUploadsStatus as StatusComponent;
    case 'timeline':
      return CoachSettingsWidgetTimelineStatus as StatusComponent;
    case 'status':
      return CoachSettingsWidgetStatusStatus as StatusComponent;
    default:
      return null;
  }
};

export type { GotoLinksSettingsValue, ActionsSettingsValue, PictureAndTagsSettingsValue, DetailsContactSettingsValue, DetailsBasicSettingsValue, DetailsExtraSettingsValue, ConsentInformationSettingsValue, NotificationInformationSettingsValue, MollieLinksSmallSettingsValue, AppointmentsSmallSettingsValue, CoursesSmallSettingsValue, QuestionnairesSmallSettingsValue, FilesSmallSettingsValue, MeasuresSmallSettingsValue, DiarySmallSettingsValue, CommunicationSmallSettingsValue, BasicIntakeSettingsValue, BasicTreatmentSettingsValue, BasicMeasureUploadsSettingsValue, BasicAdviceSettingsValue, BasicAdviceUploadsSettingsValue, TimelineSettingsValue, StatusSettingsValue };
